// Framework-agnostic toast bus.
//
// Works from BOTH React components and plain Three.js modules: it just
// dispatches a window CustomEvent that <ToastHost /> listens for and renders.
// This keeps the non-React ThreeJs/* modules free of any React dependency and
// lets every toast share the same on-brand gradient styling.

export const TOAST_EVENT = "APP_TOAST";
export const TOAST_DISMISS_EVENT = "APP_TOAST_DISMISS";

export const ToastTypes = {
  SUCCESS: "success",
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
  LOADING: "loading",
};

let counter = 0;
function nextId() {
  counter += 1;
  return `toast-${Date.now()}-${counter}`;
}

/**
 * Fire a toast notification.
 * @param {string} message      Main line (e.g. "Successfully placed 6 panels").
 * @param {object} [options]
 * @param {"success"|"info"|"warning"|"error"|"loading"} [options.type="success"]
 * @param {string} [options.description] Optional second line.
 * @param {number} [options.duration=3000] Auto-dismiss in ms (0 = sticky).
 * @param {string} [options.key] Stable id; re-using it replaces the existing
 *                               toast (used to upgrade a loading toast in place).
 * @param {{label: string, onClick: function}} [options.action] Optional action
 *                               button (e.g. Retry). Clicking it runs onClick
 *                               and dismisses the toast.
 * @returns {string} The toast id/key (pass to dismissToast()).
 */
export function showToast(message, options = {}) {
  if (typeof window === "undefined" || !message) return null;

  const id = options.key || nextId();
  const detail = {
    id,
    message,
    description: options.description || "",
    type: options.type || ToastTypes.SUCCESS,
    duration: typeof options.duration === "number" ? options.duration : 3000,
    action:
      options.action && typeof options.action.onClick === "function"
        ? { label: options.action.label || "Retry", onClick: options.action.onClick }
        : null,
  };

  window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail }));
  return id;
}

/** Remove a toast by its id/key. */
export function dismissToast(key) {
  if (typeof window === "undefined" || !key) return;
  window.dispatchEvent(
    new CustomEvent(TOAST_DISMISS_EVENT, { detail: { id: key } })
  );
}

// Convenience helpers so call-sites read nicely: toast.success("Segment added")
export const toast = {
  success: (message, options) =>
    showToast(message, { ...options, type: ToastTypes.SUCCESS }),
  info: (message, options) =>
    showToast(message, { ...options, type: ToastTypes.INFO }),
  warning: (message, options) =>
    showToast(message, { ...options, type: ToastTypes.WARNING }),
  // Errors are more important than the 3s default — keep them up for 10s so
  // users have time to read them. Callers can still override `duration`.
  error: (message, options) =>
    showToast(message, { duration: 10000, ...options, type: ToastTypes.ERROR }),
  // Sticky by default (duration 0); returns the key so you can dismiss it.
  loading: (message, options) =>
    showToast(message, { duration: 0, ...options, type: ToastTypes.LOADING }),
  dismiss: dismissToast,
};

export default toast;
